import React, { useState, useEffect, useRef, useMemo } from "react";
import { Col, ListGroup } from "react-bootstrap";
import "components/custom.css";
import "components/reorder.css";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
  useDroppable,
  rectIntersection
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Sortable card component for DND Kit
const SortableCard = ({ card, disabled, isTarget = false }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: `card-${card.id}`, disabled });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={`card-wrapper ${isTarget ? 'target-card' : 'source-card'} ${disabled ? 'disabled' : ''}`}
    >
      <div className={`${isTarget ? 'target-list-item' : 'source-list-item'}`}>
        <p className="m-0">{card.content || card.description}</p>
      </div>
    </div>
  );
};

const SortableItem = ({ id, value }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <ListGroup.Item
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
    >
      {value}
    </ListGroup.Item>
  );
};

const SortableList = ({ items }) => {
  return (
    <SortableContext
      items={items.map(item => item.id)}   // IMPORTANT
      strategy={verticalListSortingStrategy}
    >
      <ListGroup>
        {items.map(item => (
          <SortableItem
            key={item.id}
            id={item.id}
            value={item.options}
          />
        ))}
      </ListGroup>
    </SortableContext>
  );
};

// Droppable column component
const DroppableColumn = ({ column, children, isOver, isTarget = false }) => {
  const { setNodeRef, is_Over } = useDroppable({
    id: `column-${column.id}`,
  });
  return (
    <div 
      ref={setNodeRef}
      className={`droppable-column w-dragging-container ${isTarget ? 'target-column' : 'source-column'} ${isOver ? 'column-over' : ''}`}
      data-column-id={`column-${column.id}`}
    >
      <div className="column-header">
        <h5>{column.title}</h5>
      </div>
      <div className="column-content p-2 mock-resource-text">
        {children}
      </div>
    </div>
  );
};

const Reorder = (props) => {
  const [secondCount, setSecondCount] = useState(1200);
  const [questionId, setQuestionId] = useState("");
  const [reset, setReset] = useState(false);
  const [is_answer, setIsAnswer] = useState(false);
  const [items, setItems] = useState([]);
  const [sortedItems, setSortedItems] = useState([]);
  const [board, setBoard] = useState({});
  const [resultObj, setResultObj] = useState({});
  const [correctFlag, setCorrectFlag] = useState(true);
  const [activeId, setActiveId] = useState(null);
  const [activeCard, setActiveCard] = useState(null);
  const [overColumnId, setOverColumnId] = useState(null);

  const intervalIdRef = useRef(null);
  const recordIdRef = useRef(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 3,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    return () => {
      clearInterval(intervalIdRef.current);
      clearInterval(recordIdRef.current);
    };
  }, []);

  const showAnswer = () => {
    if (props.answer != is_answer) {
      if (!props.resultMode) {
        const sourceHeader = document.querySelector('div[data-rbd-draggable-id="lane-draggable-1"] > div > div:first-child');
        if (sourceHeader) {
          sourceHeader.innerHTML = '<span>Correct Order</span>';
        }
      }

      setIsAnswer(true);
      
      setTimeout(() => {
        if (!props.resultMode) {
          let elemArr = document.querySelectorAll('.list-group-item1');
          for (var i = 0; i < elemArr.length; i++) {
            elemArr[i].classList.add('cross');
          }
          if (resultObj.answer) {
            let correct = resultObj.correct;
            let answer = resultObj.answer;
            for (var i = 0; i < answer.length - 1; i++) {
              for (var j = 0; j < correct.length - 1; j++) {
                if (correct.length > i + 1) {
                  if (answer[i] + '-' + answer[i + 1] == correct[j] + '-' + correct[j + 1]) {
                    elemArr[i].classList.remove('cross');
                  }
                }
              }
            }
          }
        }
      }, 0);
    }
  }

  useEffect(() => {
    let temp = [];
    if (props.data) {
      if ((questionId && questionId !== props.data.id) || props.restart) {
        clearInterval(intervalIdRef.current);
        clearInterval(recordIdRef.current);
        temp = props.data.option.slice();
        let temp1 = props.data.option.sort(function (a, b) { return b.id - a.id });
        let newBoard = {
          columns: [
            {
              id: 1,
              title: 'Source',
              cards: temp1.map((item) => ({ 
                id: item.id, 
                title: '', 
                description: item.options, 
                content: item.options 
              })),
            },
            {
              id: 2,
              title: 'Target',
              cards: []
            }
          ]
        };

        setActiveId(null);
        setActiveCard(null);
        setOverColumnId(null);
        setCorrectFlag(true);
        setSecondCount(1200);
        setQuestionId(props.data.id);
        setReset(true);
        setItems(props.data.option);
        setBoard(newBoard);
        setSortedItems(temp.sort(function (a, b) { return a.index - b.index }));
      } else {
        if (!questionId) {
          setCorrectFlag(true);
          setQuestionId(props.data.id);
        }
      }
    }
  }, [props.data, props.restart, questionId]);

  const updateCorrect = () => {
    if (sortedItems.length !== 0) {
      const newResultObj = {
        'answer': [],
        'correct': sortedItems.sort(function (a, b) { return a.index - b.index }).map((val) => val.id)
      };
      props.submit(newResultObj);
      setCorrectFlag(false);
      setResultObj(newResultObj);
    }
  }

  const stopTest = () => {
    props.nextQuestion(resultObj);
  }

  useEffect(() => {
    if (props.stopRecording) {
      stopTest();
    }
  }, [props.stopRecording]);

  useEffect(() => {
    if (props.restart) {
      props.restartQue();
    }
  }, [props.restart]);

  const handleDragStart = (event) => {
    const { active } = event;
    setActiveId(active.id);
    
    // Find the active card
    const cardId = parseInt(active.id.toString().replace('card-', ''));
    let foundCard = null;
    
    board.columns.forEach(column => {
      const card = column.cards.find(c => c.id === cardId);
      if (card) {
        foundCard = card;
      }
    });
    
    setActiveCard(foundCard);
  };

  const handleDragOver = (event) => {
    const { over } = event;
    if (over && over.id.toString().startsWith('column-')) {
      setOverColumnId(over.id);
    } else {
      setOverColumnId(null);
    }
  };

  // const handleDragEnd = (event) => {
  //   const { active, over } = event;
    
  //   setActiveId(null);
  //   setActiveCard(null);
  //   setOverColumnId(null);

  //   if (!over) {
  //     return;
  //   }

  //   if (is_answer) {
  //     return;
  //   }

  //   const activeId = active.id.toString();
  //   const overId = over.id.toString();
    
  //   // Get card ID from the string ID
  //   const getCardIdFromString = (idString) => {
  //     const match = idString.match(/card-(\d+)/);
  //     return match ? parseInt(match[1]) : parseInt(idString);
  //   };
    
  //   const activeCardId = getCardIdFromString(activeId);
    
  //   // Find source column
  //   const sourceColumnIndex = board.columns.findIndex(column => 
  //     column.cards.some(card => card.id === activeCardId)
  //   );

  //   if (sourceColumnIndex === -1) {
  //     return;
  //   }

  //   // Create a new board copy
  //   const newColumns = [...board.columns];
    
  //   // Check if dropping on a column
  //   if (overId.startsWith('column-')) {
  //     const overColumnId = parseInt(overId.replace('column-', ''));
  //     const overColumnIndex = newColumns.findIndex(col => col.id === overColumnId);
      
  //     if (overColumnIndex !== -1 && sourceColumnIndex !== overColumnIndex) {
  //       // Move card between columns
  //       const cardIndex = newColumns[sourceColumnIndex].cards.findIndex(card => card.id === activeCardId);
        
  //       if (cardIndex !== -1) {
  //         const [movedCard] = newColumns[sourceColumnIndex].cards.splice(cardIndex, 1);
  //         newColumns[overColumnIndex].cards.push(movedCard);
  //         setBoard({ columns: newColumns });
  //         submitResult(newColumns);
  //       }
  //     }
  //   } 
  //   // Check if dropping on another card
  //   else if (overId.startsWith('card-')) {
  //     const overCardId = getCardIdFromString(overId);
  //     const overColumnIndex = newColumns.findIndex(column => 
  //       column.cards.some(card => card.id === overCardId)
  //     );
      
  //     // If dragging within the same column (reordering in target column)
  //     if (sourceColumnIndex === overColumnIndex && sourceColumnIndex === 1) {
  //       const oldIndex = newColumns[1].cards.findIndex(card => card.id === activeCardId);
  //       const newIndex = newColumns[1].cards.findIndex(card => card.id === overCardId);
        
  //       if (oldIndex !== newIndex) {
  //         newColumns[1].cards = arrayMove(newColumns[1].cards, oldIndex, newIndex);
  //         setBoard({ columns: newColumns });
  //         submitResult(newColumns);
  //       }
  //     }
  //     // If dragging from source to target (dropping on a card in target)
  //     else if (sourceColumnIndex === 0 && overColumnIndex === 1) {
  //       const cardIndex = newColumns[0].cards.findIndex(card => card.id === activeCardId);
  //       const overIndex = newColumns[1].cards.findIndex(card => card.id === overCardId);
        
  //       if (cardIndex !== -1) {
  //         const [movedCard] = newColumns[0].cards.splice(cardIndex, 1);
  //         // Insert at the position of the card we're dropping over
  //         newColumns[1].cards.splice(overIndex, 0, movedCard);
  //         setBoard({ columns: newColumns });
  //         submitResult(newColumns);
  //       }
  //     }
  //     // If dragging from target to source (dropping on a card in source)
  //     else if (sourceColumnIndex === 1 && overColumnIndex === 0) {
  //       const cardIndex = newColumns[1].cards.findIndex(card => card.id === activeCardId);
  //       const overIndex = newColumns[0].cards.findIndex(card => card.id === overCardId);
        
  //       if (cardIndex !== -1) {
  //         const [movedCard] = newColumns[1].cards.splice(cardIndex, 1);
  //         // Insert at the position of the card we're dropping over
  //         newColumns[0].cards.splice(overIndex, 0, movedCard);
  //         setBoard({ columns: newColumns });
  //         submitResult(newColumns);
  //       }
  //     }
  //   }
  // };

  // const handleDragEnd = (event) => {
  //   const { active, over } = event;

  //   setActiveId(null);
  //   setActiveCard(null);
  //   setOverColumnId(null);

  //   if (!over || is_answer) return;

  //   const activeCardId = parseInt(active.id.toString().replace("card-", ""));
  //   const newColumns = [...board.columns];

  //   // Find source column
  //   const sourceColumnIndex = newColumns.findIndex((column) =>
  //     column.cards.some((card) => card.id === activeCardId)
  //   );

  //   if (sourceColumnIndex === -1) return;

  //   // Handle dropping on column
  //   if (over.id.toString().startsWith("column-")) {
  //     const targetColumnId = parseInt(
  //       over.id.toString().replace("column-", "")
  //     );
  //     const targetColumnIndex = newColumns.findIndex(
  //       (col) => col.id === targetColumnId
  //     );

  //     if (targetColumnIndex !== -1 && sourceColumnIndex !== targetColumnIndex) {
  //       const cardIndex = newColumns[sourceColumnIndex].cards.findIndex(
  //         (card) => card.id === activeCardId
  //       );
  //       const [movedCard] = newColumns[sourceColumnIndex].cards.splice(
  //         cardIndex,
  //         1
  //       );
  //       newColumns[targetColumnIndex].cards.push(movedCard);

  //       setBoard({ columns: newColumns });
  //       submitResult(newColumns);
  //     }
  //   }
  // };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    
    setActiveId(null);
    setActiveCard(null);
    setOverColumnId(null);

    if (!over || is_answer) return;

    const activeCardId = parseInt(active.id.toString().replace("card-", ""));
    const newColumns = [...board.columns];

    // Find source column
    const sourceColumnIndex = newColumns.findIndex((column) =>
      column.cards.some((card) => card.id === activeCardId)
    );
    if (sourceColumnIndex === -1) return;
    
    // Handle dropping on column (append to end)
    if (over.id.toString().startsWith("column-")) {
      const targetColumnId = parseInt(
        over.id.toString().replace("column-", "")
      );
      const targetColumnIndex = newColumns.findIndex(
        (col) => col.id === targetColumnId
      );
      if (targetColumnIndex !== -1 && sourceColumnIndex !== targetColumnIndex) {
        const cardIndex = newColumns[sourceColumnIndex].cards.findIndex(
          (card) => card.id === activeCardId
        );
        // console.log("active",active,"over",over,"targetColumnId",targetColumnId,"newColumns",
        //   newColumns,"targetColumnIndex",targetColumnIndex,"sourceColumnIndex",sourceColumnIndex,
        //   "cardIndex",cardIndex);
        // console.log(newColumns,movedCard);
        
        const [movedCard] = newColumns[sourceColumnIndex].cards.splice(
          cardIndex,
          1
        );
        // newColumns[targetColumnIndex].cards.push(movedCard);
        newColumns[targetColumnIndex].cards.splice( targetColumnId, 0, movedCard);
        setBoard({ columns: newColumns });
        submitResult(newColumns);
      }
    }
    // Handle dropping on specific card (insert at position)
    else if (over.id.toString().startsWith("card-")) {
      const overCardId = parseInt(over.id.toString().replace("card-", ""));
      const targetColumnIndex = newColumns.findIndex((column) =>
        column.cards.some((card) => card.id === overCardId)
      );

      if (targetColumnIndex !== -1) {
        const cardIndex = newColumns[sourceColumnIndex].cards.findIndex(
          (card) => card.id === activeCardId
        );
        const targetIndex = newColumns[targetColumnIndex].cards.findIndex(
          (card) => card.id === overCardId
        );
        // Check if dropping on the last card in target column
        const isLastCard = targetIndex === newColumns[targetColumnIndex].cards.length - 1;
        
        // Remove from source
        const [movedCard] = newColumns[sourceColumnIndex].cards.splice(
          cardIndex,
          1
        );

      const insertIndex = isLastCard ? newColumns[targetColumnIndex].cards.length : targetIndex;
      // console.log("Last Checking -> ","isLastCard",isLastCard,"insertIndex",insertIndex,"newColumns",newColumns,
      //   "targetIndex",targetIndex,"targetColumnIndex",targetColumnIndex,"sourceColumnIndex",sourceColumnIndex,
      //   "cardIndex",cardIndex);
        
      // // Insert at target position
      // if (sourceColumnIndex === targetColumnIndex) {
      //   // Reordering within same column
      //   newColumns[targetColumnIndex].cards.splice(insertIndex, 0, movedCard);
      // } else {
      //   // Moving between columns - insert at specific position
      //   newColumns[targetColumnIndex].cards.splice(insertIndex, 0, movedCard);
      // }
        newColumns[targetColumnIndex].cards.splice( insertIndex === 3 ? insertIndex : targetIndex, 0, movedCard);
        setBoard({ columns: newColumns });
        submitResult(newColumns);
      }
    }
  };
  
  const submitResult = (columns) => {
    props.showResult?.();
    
    const newResultObj = {
      'answer': columns[1].cards.map((val) => val.id),
      'correct': sortedItems.map((val) => val.id)
    };
    
    props.submit?.(newResultObj, true);
    setResultObj(newResultObj);
  };

  let displayBoard = {
    columns: [
      {
        id: 1,
        title: 'Source',
        cards: [],
      },
      {
        id: 2,
        title: 'Target',
        cards: []
      }
    ]
  };
  let displayQuestion = "";
  let temp = [];

  if (props.data) {
    if (correctFlag) {
      updateCorrect();
    }
    displayQuestion = props.data.question;
    displayBoard = {
      columns: [
        {
          id: 1,
          title: 'Source',
          cards: props.data.option.map((item) => ({ 
            id: item.id, 
            title: '', 
            description: item.options, 
            content: item.options 
          })),
        },
        {
          id: 2,
          title: 'Target',
          cards: []
        }
      ]
    };
    if (JSON.stringify(board) === '{}') {
      setBoard(displayBoard);
    } else {
      displayBoard = board;
    }
    if (items.length === 0) {
      temp = props.data.option.slice();
      setItems(props.data.option);
      setSortedItems(temp.sort(function (a, b) { return a.index - b.index }));
    }
  }

  useEffect(() => {
    if (reset) {
      setReset(false);
      setIsAnswer(false);
    }
  }, [reset]);

  const targetCardIds = useMemo(() => 
    board.columns && board.columns[1] 
      ? board.columns[1].cards.map(c => `card-${c.id}`)
      : [],
    [board.columns]
  );

  // const sourceCardIds = useMemo(() => 
  //   board.columns && board.columns[0] 
  //     ? board.columns[0].cards.map(c => `card-${c.id}`)
  //     : [],
  //   [board.columns]
  // );

  if (props.answer) {
    let temp = props.data.option;    
    let lane0 = [...temp.sort(function (a, b) { return a.index - b.index }).map((item) => ({ 
      id: item.id, 
      title: '', 
      description: item.options, 
      content: item.options 
    }))];
    displayBoard.columns[0] = {
      id: 1,
      title: 'Correct Order',
      cards: lane0,
    };
    showAnswer();
  }

  return (
    <>
      {!props.resultMode && 
        <Col xs={12}>
          <h4 className="infoText"></h4>
        </Col>
      }
      <Col xs={12}><h5>{displayQuestion}</h5></Col>
      <Col xs={12}>
        <h3 className="question">{props.data && props.data.question_mcq}</h3>
      </Col>
      
      {!props.resultMode && <Col lg={12}>&nbsp;</Col>}
      <Col lg={12}>
        {props.data && !props.resultMode &&
          <div className="reorder-board">
            <div className="visibility-visible mx-0 w-100">
              {board && Array.isArray(board.columns) && board.columns.length > 0 ? (
                <>
                  <DndContext
                    sensors={sensors}
                    collisionDetection={rectIntersection}
                    onDragStart={handleDragStart}
                    onDragOver={handleDragOver}
                    onDragEnd={handleDragEnd}
                  >
                    <div className="d-flex flex-lg-row w-100" style={{ gap: '20px' }}>
                      {/* Source Column */}
                      <DroppableColumn 
                        column={board.columns[0]} 
                        isOver={overColumnId === `column-${board.columns[0].id}`}
                        isTarget={false}
                      >
                        <div className="source-list p-2">
                          {board.columns[0].cards.map(card => (
                            <SortableCard 
                              key={card.id} 
                              card={card} 
                              disabled={!!props.answer || is_answer}
                              isTarget={false}
                            />
                          ))}
                        </div>
                      </DroppableColumn>

                      {/* Target Column */}
                      <DroppableColumn 
                        column={board.columns[1]} 
                        isOver={overColumnId === `column-${board.columns[1].id}`}
                        isTarget={true}
                      >
                        <SortableContext
                          items={targetCardIds}
                          strategy={verticalListSortingStrategy}
                        >
                          <div className="target-list">
                            {board.columns[1].cards.map(card => (
                              <SortableCard 
                                key={card.id} 
                                card={card} 
                                disabled={!!props.answer || is_answer}
                                isTarget={true}
                              />
                            ))}
                          </div>
                        </SortableContext>
                      </DroppableColumn>
                    </div>

                    {/* Drag Overlay */}
                    <DragOverlay>
                      {activeCard ? (
                        <div className="card-overlay">
                          <div className="card-wrapper">
                            <div className="source-list-item">
                              <p>{activeCard.content || activeCard.description}</p>
                            </div>
                          </div>
                        </div>
                      ) : null}
                    </DragOverlay>
                  </DndContext>
                </>
              ) : (
                <p>Loading board...</p>
              )}
            </div>
          </div>
        }
      </Col>
      {!props.resultMode && <Col lg={12}>&nbsp;</Col>}

      <Col lg={12}>
        {is_answer && !props.resultMode && <h3>Result: </h3>}
        {is_answer &&
          <div className="col-lg-6">
            <ul className="connectList">
              {board.columns && board.columns[1].cards.map((v, k) => {
                return <li key={k} className="list-group-item1">{v.description}</li>
              })}
            </ul>
          </div>
        }
        {/* {props.resultMode && <SortableList items={sortedItems} />} */}
      </Col>
    </>
  );
}

export default Reorder;